import api from './api';
import type {
  PaginatedResponse,
  GuardListItem,
  GuardDetail,
  SiteListItem,
  SiteDetail,
  OrganizationDetail,
  TeamMember,
  InviteItem,
  VendorConnection,
  UploadListItem,
  UploadCreateResponse,
  ParsedEventListItem,
  PlatformInvite,
  PatrolServicePlan,
  VisitVerification,
  PatrolException,
  PatrolPlanChangeRequest,
} from '../types';

// Guards
export const vendorApi = {
  // Guards
  getGuards(params?: Record<string, string>) {
    return api.get<PaginatedResponse<GuardListItem>>('/vendor/guards/', { params });
  },
  getGuard(id: string) {
    return api.get<GuardDetail>(`/vendor/guards/${id}/`);
  },
  createGuard(data: { first_name: string; last_name: string; external_id?: string; email?: string; phone?: string }) {
    return api.post<GuardDetail>('/vendor/guards/', data);
  },
  updateGuard(id: string, data: Record<string, unknown>) {
    return api.patch<GuardDetail>(`/vendor/guards/${id}/`, data);
  },
  deleteGuard(id: string) {
    return api.delete<{ deleted: 'hard' | 'soft'; detail: string }>(`/vendor/guards/${id}/`);
  },
  assignGuardToSites(guardId: string, siteIds: string[]) {
    return api.post<{ assigned: string[]; already_assigned: string[] }>(`/vendor/guards/${guardId}/assign/`, { site_ids: siteIds });
  },
  unassignGuard(guardId: string, siteId: string) {
    return api.post<{ detail: string }>(`/vendor/guards/${guardId}/unassign/`, { site_id: siteId });
  },
  reassignGuard(guardId: string, fromSiteId: string, toSiteId: string) {
    return api.post<{ detail: string }>(`/vendor/guards/${guardId}/reassign/`, { from_site_id: fromSiteId, to_site_id: toSiteId });
  },

  // Sites
  getSites(params?: Record<string, string>) {
    return api.get<PaginatedResponse<SiteListItem>>('/vendor/sites/', { params });
  },
  getSite(id: string) {
    return api.get<SiteDetail>(`/vendor/sites/${id}/`);
  },
  acceptSite(id: string, action: 'accept' | 'reject') {
    return api.patch<SiteDetail>(`/vendor/sites/${id}/accept/`, { action });
  },
  suggestSiteContextChange(siteId: string, suggestion: string) {
    return api.post(`/vendor/sites/${siteId}/context/suggest-change/`, { suggestion });
  },

  // Organization
  getOrganization() {
    return api.get<OrganizationDetail>('/vendor/organization/');
  },
  updateOrganization(data: Record<string, unknown>) {
    return api.patch<OrganizationDetail>('/vendor/organization/', data);
  },

  // Team
  getTeam(params?: Record<string, string>) {
    return api.get<PaginatedResponse<TeamMember>>('/vendor/team/', { params });
  },

  // Invites
  getInvites() {
    return api.get<InviteItem[]>('/vendor/invites/');
  },
  createInvite(data: { email: string; org_role: 'member' | 'admin' }) {
    return api.post<InviteItem>('/vendor/invites/', data);
  },

  // Invitation Stats
  getInvitationStats() {
    return api.get<{
      total: number;
      pending_count: number;
      active_count: number;
      rejected_count: number;
      acceptance_rate: number;
      avg_response_hours: number;
    }>('/vendor/invitation-stats/');
  },

  // Feature 7: pending critical-override reviews affecting this vendor.
  // Count-only — vendors cannot see the underlying triggers per spec.
  getPendingReviewCount() {
    return api.get<{ count: number }>('/vendor/pending-review-count/');
  },

  // Connections
  getConnections(params?: Record<string, string>) {
    return api.get<PaginatedResponse<VendorConnection>>('/vendor/connections/', { params });
  },
  respondToConnection(id: string, action: 'accept' | 'reject') {
    return api.patch<VendorConnection>(`/vendor/connections/${id}/respond/`, { action });
  },

  // Dashboard
  getWeeklySummary() {
    return api.get<{
      total_patrols: number;
      total_incidents: number;
      reports_submitted: number;
      avg_patrol_gap: number;
      week_start: string;
    }>('/vendor/weekly-summary/');
  },
  getTopGuards(limit = 5) {
    return api.get<Array<{ id: string; name: string; score: number }>>('/vendor/top-guards/', { params: { limit: String(limit) } });
  },
  getRecentIssues(limit = 10) {
    return api.get<Array<{
      id: string;
      rule_name: string;
      rule_category: string;
      severity_level: string;
      is_positive: boolean;
      entity_type: string;
      entity_name: string;
      score_impact: number;
      explanation: string;
      created_at: string;
    }>>('/vendor/recent-issues/', { params: { limit: String(limit) } });
  },

  // Platform Invites (cross-role)
  sendPlatformInvite(email: string) {
    return api.post<PlatformInvite>('/auth/platform-invites/', { email });
  },
  getPlatformInvites() {
    return api.get<PlatformInvite[]>('/auth/platform-invites/');
  },

  // Uploads
  getUploads(params?: Record<string, string>) {
    return api.get<PaginatedResponse<UploadListItem>>('/vendor/uploads/', { params });
  },
  getUpload(id: string) {
    return api.get<UploadListItem>(`/vendor/uploads/${id}/`);
  },
  uploadFile(file: File, siteId: string) {
    const formData = new FormData();
    formData.append('file', file);
    formData.append('site_id', siteId);
    // Returns 202 Accepted; upload runs in a background thread on the
    // server.  Poll getUpload / getUploads for status transitions.
    return api.post<UploadCreateResponse>('/vendor/uploads/', formData);
  },
  getUploadEvents(uploadId: string, params?: Record<string, string>) {
    return api.get<PaginatedResponse<ParsedEventListItem>>(
      `/vendor/uploads/${uploadId}/events/`, { params },
    );
  },

  // Patrol plans (read-only + acknowledge)
  getPatrolPlans(params?: Record<string, string>) {
    return api.get<PaginatedResponse<PatrolServicePlan>>('/vendor/patrol-plans/', { params });
  },
  acknowledgePatrolPlan(planId: string) {
    return api.post<PatrolServicePlan>(`/vendor/patrol-plans/${planId}/acknowledge/`);
  },
  // Sprint 3: read verifications + exceptions; submit + withdraw
  // dispute requests.
  getVisitVerifications(params: Record<string, string>) {
    return api.get<PaginatedResponse<VisitVerification>>(
      '/vendor/visit-verifications/', { params },
    );
  },
  getPatrolExceptions(params?: Record<string, string>) {
    return api.get<PaginatedResponse<PatrolException>>(
      '/vendor/patrol-exceptions/', { params },
    );
  },
  submitPatrolChangeRequest(data: { plan_id: string; proposal_text: string }) {
    return api.post<PatrolPlanChangeRequest>('/vendor/patrol-change-requests/', data);
  },
  getPatrolChangeRequests(params?: Record<string, string>) {
    return api.get<PaginatedResponse<PatrolPlanChangeRequest>>(
      '/vendor/patrol-change-requests/', { params },
    );
  },
  withdrawPatrolChangeRequest(id: string) {
    return api.delete(`/vendor/patrol-change-requests/${id}/`);
  },
};
